import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import BranchService from "src/Services/API/branch.service";
import BranchButton from "./BranchButton";
import { isSuperAdmin } from "src/Common/function";
import UserService from "src/Services/API/user.service";

const BranchBar = ({ allEnabled = true, availableBranches }) => {
  const currentUser = UserService.getCurrentUser();
  const dispatch = useDispatch();
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false);
  const buttonRefs = useRef([]);
  // Position/size of the sliding pill that sits behind the selected branch.
  const [pillStyle, setPillStyle] = useState({ left: 0, top: 0, width: 0, height: 0, visible: false });

  useEffect(() => {
    BranchService.getAll()
      .then((response) => {
        let temp = [];
        if (allEnabled) {
          temp = [{ id: 0, name: "All" }];
        }
        // The backend already returns only the active company's branches, so
        // this narrows by branch membership: a user sees the branches they
        // belong to. A Super Admin inside a tenant they entered belongs to none
        // of its branches — their own are in their home company — so narrowing
        // them would empty the bar. The company scope above is the boundary that
        // matters for them.
        const own = response.data.filter((b) => (currentUser.branches || []).some((b2) => b2.id == b.id));
        temp = temp.concat(isSuperAdmin() ? response.data : own);
        setBranches(temp);
      })
      .catch((error) => {
        console.log("getting branch error: ", error);
      });
  }, []);

  useEffect(() => {
    if (branches.length > 0) {
      console.log("AAA: ", branch);
      // if (isManagerOver() || isAccountant()) {
      if (branch) {
        branchClicked(branches.findIndex((b) => b.id == branch));
      } else {
        console.log("branch is 0");
        branchClicked(0);
      }
      // }
    }
  }, [branches]);

  const branchClicked = (index) => {
    let url = "";
    if (branches[index].name === "All") {
      if (navigationType === "POP") {
        url = appendQueryParam("branch", "0");
      } else {
        url = appendQueryParam("branch", "0", "criteria");
      }
    } else {
      if (navigationType === "POP") {
        url = appendQueryParam("branch", branches[index].id);
      } else {
        url = appendQueryParam("branch", branches[index].id, "criteria");
      }
    }
    navigate(url, { replace: true });
  };

  // Move the sliding pill to sit behind the currently selected branch button.
  useLayoutEffect(() => {
    const selectedIndex = branches.findIndex((b) => b.id == branch);
    const el = buttonRefs.current[selectedIndex];
    if (!el) {
      setPillStyle((prev) => ({ ...prev, visible: false }));
      return undefined;
    }

    const measurePill = () => {
      setPillStyle({
        left: el.offsetLeft,
        top: el.offsetTop,
        width: el.offsetWidth,
        height: el.offsetHeight,
        visible: true,
      });
    };
    measurePill();

    const ro = new ResizeObserver(measurePill);
    ro.observe(el);
    if (scrollRef.current) ro.observe(scrollRef.current);
    return () => ro.disconnect();
  }, [branches, branch]);

  // Show the scroll chevron only when the row overflows its container.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const measure = () => {
      setCanScroll(el.scrollWidth - el.clientWidth > el.scrollLeft + 1);
    };
    measure();

    el.addEventListener("scroll", measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [branches]);

  const scrollRight = () => {
    const el = scrollRef.current;
    if (el) {
      el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" });
    }
  };

  const appendQueryParam = (query, value, queryToRemove) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;

    const url = new URLSearchParams(currentSearch);
    url.set(query, value); // replaces or adds branch=0
    if (queryToRemove) {
      url.delete(queryToRemove);
    }

    return `${currentPath}?${url.toString()}`;
  };

  return (
    <div className="w-full flex items-center">
      <div
        ref={scrollRef}
        className="relative flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide scroll-smooth"
      >
        {/* Sliding pill that animates to sit behind the selected branch. */}
        <div
          aria-hidden="true"
          className="absolute rounded-[17px] bg-[#0065FD] transition-all duration-300 ease-in-out pointer-events-none"
          style={{
            left: pillStyle.left,
            top: pillStyle.top,
            width: pillStyle.width,
            height: pillStyle.height,
            opacity: pillStyle.visible ? 1 : 0,
          }}
        />
        {branches.map((b, index) => (
          <BranchButton
            key={index}
            ref={(el) => (buttonRefs.current[index] = el)}
            title={b.name}
            index={index}
            clicked={branchClicked}
            selected={branch == b.id}
          />
        ))}
      </div>
      {canScroll && (
        <button
          type="button"
          onClick={scrollRight}
          aria-label="Scroll branches"
          className="flex-shrink-0 ml-1 flex items-center justify-center w-6 h-6 text-titleColor focus:outline-none"
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 1L7 7L1 13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default BranchBar;
